import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Toast } from '../ui/Toast';
import { ImageDropzone } from '../ui/ImageDropzone';
import { api, getUploadsPublicUrl } from '../../services/api';
import type { DeliveryChecklistData } from '../../types/delivery-checklist';

type TriState = true | false | null;

type SectionConfig = {
  key: keyof DeliveryChecklistData;
  label: string;
  items: Array<{ key: string; label: string }>;
};

export const DELIVERY_CHECKLIST_SECTIONS: SectionConfig[] = [
  {
    key: 'puertasYCerraduras',
    label: 'Puertas y cerraduras',
    items: [
      { key: 'puertasAbrenCierran', label: 'Puertas abren y cierran correctamente' },
      { key: 'cerradurasFuncionan', label: 'Cerraduras funcionan bien' },
      { key: 'bisagrasFiremes', label: 'Bisagras firmes y sin ruido' },
      { key: 'puertasSinDanos', label: 'Puertas sin golpes o daños' },
    ],
  },
  {
    key: 'instalacionElectrica',
    label: 'Instalación eléctrica',
    items: [
      { key: 'apagadoresFuncionan', label: 'Apagadores funcionan correctamente' },
      { key: 'contactosTieneCorriente', label: 'Contactos tienen corriente' },
      { key: 'focosCompletos', label: 'Focos/Lámparas completos' },
      { key: 'sinCablesExpuestos', label: 'Sin cables expuestos' },
    ],
  },
  {
    key: 'plomeria',
    label: 'Plomería',
    items: [
      { key: 'llavesAguaFuncionan', label: 'Llaves de agua funcionan' },
      { key: 'sinFugas', label: 'Sin fugas' },
      { key: 'wcFunciona', label: 'WC funciona correctamente' },
    ],
  },
  {
    key: 'cocinaElectrodomesticos',
    label: 'Cocina / Electrodomésticos',
    items: [
      { key: 'estufaFunciona', label: 'Estufa funciona' },
      { key: 'campanaFunciona', label: 'Campana/extractor funciona' },
      { key: 'refrigeradorFunciona', label: 'Refrigerador enfría' },
      { key: 'microondasFunciona', label: 'Microondas funciona' },
    ],
  },
  {
    key: 'areaLavado',
    label: 'Área de lavado',
    items: [
      { key: 'lavadoraFunciona', label: 'Lavadora funciona' },
      { key: 'secadoraFunciona', label: 'Secadora funciona' },
      { key: 'conexionesBuenEstado', label: 'Conexiones en buen estado' },
    ],
  },
  {
    key: 'ventanas',
    label: 'Ventanas',
    items: [
      { key: 'abrenCierran', label: 'Abren y cierran correctamente' },
      { key: 'vidriosSinRoturas', label: 'Vidrios sin roturas' },
      { key: 'sellosBuenEstado', label: 'Sellos en buen estado' },
    ],
  },
  {
    key: 'pinturaAcabados',
    label: 'Pintura y acabados',
    items: [
      { key: 'paredesLimpias', label: 'Paredes limpias y sin manchas' },
      { key: 'sinHumedadMoho', label: 'Sin humedad o moho' },
      { key: 'techosBuenEstado', label: 'Techos en buen estado' },
    ],
  },
  {
    key: 'pisos',
    label: 'Pisos',
    items: [
      { key: 'sinLosetasRotas', label: 'Sin losetas rotas o levantadas' },
      { key: 'limpiosBuenEstado', label: 'Limpios y en buen estado' },
      { key: 'sinDesniveles', label: 'Sin desniveles peligrosos' },
      { key: 'zoclosBuenEstado', label: 'Zoclos en buen estado' },
    ],
  },
  {
    key: 'aireAcondicionado',
    label: 'Aire acondicionado / ventilación',
    items: [
      { key: 'equiposFuncionan', label: 'Equipos funcionan correctamente' },
      { key: 'sinRuidosExtranos', label: 'Sin ruidos extraños' },
    ],
  },
];

export function emptyDeliveryChecklistData(): DeliveryChecklistData {
  const makeSection = (itemKeys: string[]) => {
    const section: Record<string, TriState | string> = {};
    itemKeys.forEach((k) => {
      section[k] = null;
    });
    section.notas = '';
    return section;
  };
  return {
    puertasYCerraduras: makeSection(DELIVERY_CHECKLIST_SECTIONS[0].items.map((i) => i.key)) as any,
    instalacionElectrica: makeSection(DELIVERY_CHECKLIST_SECTIONS[1].items.map((i) => i.key)) as any,
    plomeria: makeSection(DELIVERY_CHECKLIST_SECTIONS[2].items.map((i) => i.key)) as any,
    cocinaElectrodomesticos: makeSection(DELIVERY_CHECKLIST_SECTIONS[3].items.map((i) => i.key)) as any,
    areaLavado: makeSection(DELIVERY_CHECKLIST_SECTIONS[4].items.map((i) => i.key)) as any,
    ventanas: makeSection(DELIVERY_CHECKLIST_SECTIONS[5].items.map((i) => i.key)) as any,
    pinturaAcabados: makeSection(DELIVERY_CHECKLIST_SECTIONS[6].items.map((i) => i.key)) as any,
    pisos: makeSection(DELIVERY_CHECKLIST_SECTIONS[7].items.map((i) => i.key)) as any,
    aireAcondicionado: makeSection(DELIVERY_CHECKLIST_SECTIONS[8].items.map((i) => i.key)) as any,
  };
}

interface Props {
  inspectionId: string;
  assignedUserName?: string;
  onCompleted?: () => void;
  onCancel?: () => void;
}

/** Formulario de checklist de entrega/salida: mismo flujo que usa Mantenimiento, reutilizable en la ficha del inquilino. */
export function DeliveryChecklistForm({ inspectionId, assignedUserName, onCompleted, onCancel }: Props) {
  const queryClient = useQueryClient();

  const [checklistData, setChecklistData] = useState<DeliveryChecklistData>(emptyDeliveryChecklistData());
  const [notes, setNotes] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const completeMutation = useMutation({
    mutationFn: async () => {
      let photos: string[] = [];
      if (selectedFiles.length > 0) {
        setUploading(true);
        const res = await api.uploadMaintenanceReportImages(selectedFiles);
        photos = res.fileUrls ?? [];
      }
      return api.completeDeliveryInspection(inspectionId, {
        checklistData,
        notes: notes.trim() || undefined,
        photos,
      });
    },
    onSuccess: async () => {
      setToast({ message: 'Inspección completada correctamente.', type: 'success' });
      await queryClient.invalidateQueries({ queryKey: ['my-delivery-inspections'] });
      await queryClient.invalidateQueries({ queryKey: ['delivery-inspection'] });
      onCompleted?.();
    },
    onError: (err: any) => {
      setToast({ message: err?.message || 'No se pudo completar la inspección', type: 'error' });
    },
    onSettled: () => setUploading(false),
  });

  const setItemValue = (sectionKey: keyof DeliveryChecklistData, itemKey: string, value: TriState) => {
    setChecklistData((prev: any) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [itemKey]: value,
      },
    }));
  };

  const setSectionNote = (sectionKey: keyof DeliveryChecklistData, value: string) => {
    setChecklistData((prev: any) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        notas: value,
      },
    }));
  };

  const addFiles = (files: File[]) => {
    setSelectedFiles((prev) => [...prev, ...files]);
    files.forEach((f) => {
      const reader = new FileReader();
      reader.onload = () => setPreviews((prev) => [...prev, String(reader.result || '')]);
      reader.readAsDataURL(f);
    });
  };

  const removeFile = (idx: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== idx));
    setPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-6">
      {assignedUserName && (
        <p className="text-sm text-gray-600">
          Asignado a: <strong>{assignedUserName}</strong>
        </p>
      )}

      {DELIVERY_CHECKLIST_SECTIONS.map((section) => {
        const sectionData: any = (checklistData as any)[section.key];
        return (
          <Card key={String(section.key)} title={section.label}>
            <div className="space-y-3">
              {section.items.map((item) => {
                const value = sectionData?.[item.key] as TriState;
                return (
                  <div key={item.key} className="flex items-center justify-between gap-3 border-b border-gray-100 pb-2">
                    <span className="text-sm text-gray-800">{item.label}</span>
                    <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
                      <button
                        type="button"
                        className={`px-3 py-1 text-xs ${value === true ? 'bg-green-100 text-green-800' : 'bg-white text-gray-600'}`}
                        onClick={() => setItemValue(section.key, item.key, true)}
                      >
                        ✓ Bien
                      </button>
                      <button
                        type="button"
                        className={`px-3 py-1 text-xs border-l border-gray-200 ${value === false ? 'bg-red-100 text-red-800' : 'bg-white text-gray-600'}`}
                        onClick={() => setItemValue(section.key, item.key, false)}
                      >
                        ✗ Problema
                      </button>
                      <button
                        type="button"
                        className={`px-3 py-1 text-xs border-l border-gray-200 ${value === null ? 'bg-gray-100 text-gray-800' : 'bg-white text-gray-600'}`}
                        onClick={() => setItemValue(section.key, item.key, null)}
                      >
                        — N.A.
                      </button>
                    </div>
                  </div>
                );
              })}
              <textarea
                value={sectionData?.notas || ''}
                onChange={(e) => setSectionNote(section.key, e.target.value)}
                rows={2}
                placeholder="Notas de esta sección (opcional)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          </Card>
        );
      })}

      <Card title="Notas generales y fotos">
        <div className="space-y-3">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Notas generales de la inspección..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <ImageDropzone onFilesSelected={addFiles} />
          {previews.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {previews.map((src, idx) => (
                <div key={idx} className="relative">
                  <img src={getUploadsPublicUrl(src) || src} className="w-20 h-20 object-cover rounded-lg border" />
                  <button
                    type="button"
                    className="absolute -top-2 -right-2 bg-red-500 text-white w-5 h-5 rounded-full text-xs"
                    onClick={() => removeFile(idx)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        )}
        <Button type="button" onClick={() => completeMutation.mutate()} disabled={completeMutation.isPending || uploading}>
          {completeMutation.isPending || uploading ? 'Completando...' : 'Completar inspección'}
        </Button>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
